import React from 'react';
import AntAPI from '../../utils/AntAPI';
import AntAuth from '../../utils/AntAuth';
import Stage2Round from './stages/Stage2Round';
import { validateAreas } from './components/AreaListPanel';
import {
    buildQuestionList,
    getNextQuestion,
    getVisiblePendingCount,
    applyAnswer,
    rebuildHistoryAndState,
    isQuestionVisible
} from './dependencyEngine';
import { t } from './i18n';
import './index.scss';

// V5 — KHÔNG còn round. Một danh sách câu hỏi duy nhất:
//   - sortedQuestions: build 1 lần (topo sort theo prereq).
//   - answers: { [qId]: 0|1 } — persist khi back/forward.
//   - answerHistory: [qId, ...] thứ tự user trả lời lần đầu.
//   - viewIdx: 0..answerHistory.length
//       < length → đang xem lại câu cũ tại history[viewIdx]
//       === length → đang xem câu kế tiếp (pending) từ getNextQuestion.
// Khi sửa câu cũ, cascade rebuild — câu sau invisible sẽ bị drop.
export default function AntSetupWizard(props) {
    var [loading, setLoading] = React.useState(false);
    var [error, setError] = React.useState(null);

    // Config từ BE
    var [identity, setIdentity] = React.useState(null);
    var [businessFeatures, setBusinessFeatures] = React.useState([]);
    var [currentSettings, setCurrentSettings] = React.useState({});
    var [sortedQuestions, setSortedQuestions] = React.useState([]);
    var [aiProviderReady, setAiProviderReady] = React.useState(false);

    // Wizard state
    var [settingsState, setSettingsState] = React.useState({});
    var [answers, setAnswers] = React.useState({});             // { [qId]: 0|1 }
    var [answerHistory, setAnswerHistory] = React.useState([]); // [qId, ...]
    var [viewIdx, setViewIdx] = React.useState(0);              // navigation cursor
    var [customPanelData, setCustomPanelData] = React.useState({});

    var locale = (identity && identity.locale)
        || (props.session && props.session.bus_settings && props.session.bus_settings.Sys_BaseLanguage
            ? props.session.bus_settings.Sys_BaseLanguage.split('-')[0] : 'vi');

    var API = React.useMemo(function () { return new AntAPI('setting', 'SysSetupWizard'); }, []);

    // Draft persistence — save NGAY mỗi khi state thay đổi.
    // draftReadyRef = true sau khi start() restore xong, tránh ghi đè draft vừa load.
    var draftReadyRef = React.useRef(false);

    // Bulk panels (product_table_with_ai) lưu vào localStorage — KHÔNG gửi lên BE
    // (data có thể rất lớn, user có thể pending vài tiếng ở bước nhập danh sách).
    var bulkStorageKey = React.useMemo(function () {
        var bid = (props.session && props.session.business_id) || 'anon';
        return 'ante.wizard.bulkPanels.' + bid;
    }, [props.session && props.session.business_id]);

    function getBulkPanelIds() {
        var ids = {};
        sortedQuestions.forEach(function (q) {
            if (q.type !== 'custom_panel' || !q.customPanel) return;
            var t = q.customPanel.type;
            // Panels có data lớn → lưu localStorage thay vì BE draft.
            if (t === 'product_table_with_ai' || t === 'slot_table_with_ai') {
                ids[q.id] = true;
            }
        });
        return ids;
    }

    function splitCustomPanelData(data) {
        var bulkIds = getBulkPanelIds();
        var draftPart = {};
        var bulkPart = {};
        Object.keys(data || {}).forEach(function (k) {
            if (bulkIds[k]) bulkPart[k] = data[k];
            else draftPart[k] = data[k];
        });
        return { draftPart: draftPart, bulkPart: bulkPart };
    }

    React.useEffect(function () { start(); /* eslint-disable-next-line */ }, []);

    React.useEffect(function () {
        if (!draftReadyRef.current) return;
        var split = splitCustomPanelData(customPanelData);
        // Bulk → localStorage (chỉ khi có data, tránh ghi rỗng đè bản cũ)
        try {
            if (Object.keys(split.bulkPart).length > 0) {
                localStorage.setItem(bulkStorageKey, JSON.stringify(split.bulkPart));
            }
        } catch (_) { /* localStorage full / disabled — bỏ qua */ }
        // Draft (không kèm bulk) → BE
        var payload = {
            draft: {
                answers: answers,
                customPanelData: split.draftPart,
                answerHistory: answerHistory,
                viewIdx: viewIdx,
                settingsState: settingsState
            }
        };
        API.postActionWithData('save-draft', payload).catch(function () { /* silent */ });
        /* eslint-disable-next-line */
    }, [answers, customPanelData, answerHistory, viewIdx, settingsState, sortedQuestions]);

    async function call(path, payload) {
        setLoading(true); setError(null);
        try {
            var res = await API.postActionWithData(path, payload || {});
            var data = (res && res.data) || res;
            if (data && data.error) {
                setError(typeof data.error === 'string' ? data.error : JSON.stringify(data.error));
                return null;
            }
            return data;
        } catch (e) {
            setError(e && e.message ? e.message : t(locale, 'error_generic'));
            return null;
        } finally { setLoading(false); }
    }

    async function exitWizard() {
        // Wizard không chạy được (chưa cấu hình ngành / lỗi tải) → thoát silent.
        // Không hiển thị thông báo kỹ thuật cho user.
        try {
            if (typeof props.onExit === 'function') return props.onExit();
        } catch (_) { /* ignore */ }
        try { window.location.replace('/'); } catch (_) { /* ignore */ }
    }

    async function start() {
        // KHÔNG dùng call() — không show error UI. Wizard hỏng → thoát silent.
        setLoading(true); setError(null);
        var d = null;
        try {
            var res = await API.postActionWithData('start', {});
            d = (res && res.data) || res;
        } catch (e) {
            console.warn('AntSetupWizard.start failed:', e && e.message);
        } finally { setLoading(false); }
        if (!d || d.error) { exitWizard(); return; }
        if (!Array.isArray(d.questions) || d.questions.length === 0) { exitWizard(); return; }
        var qs = d.questions || [];
        var feats = d.business_features || [];
        var cs = d.current_settings || {};
        setIdentity(d.identity || null);
        setBusinessFeatures(feats);
        setCurrentSettings(cs);
        setAiProviderReady(!!d.ai_provider_ready);

        // Restore từ draft (BE.actionStart trả d.draft nếu user chưa apply xong)
        var draft = (d && d.draft) || null;
        // Bulk panels đọc từ localStorage rồi merge vào customPanelData (BE chỉ giữ draft nhỏ).
        var bulkPart = {};
        try {
            var raw = localStorage.getItem(bulkStorageKey);
            if (raw) {
                var parsed = JSON.parse(raw);
                if (parsed && typeof parsed === 'object') bulkPart = parsed;
            }
        } catch (_) { /* corrupt — bỏ qua */ }

        if (draft && typeof draft === 'object') {
            setSettingsState(Object.assign({}, cs, draft.settingsState || {}));
            setAnswers(draft.answers || {});
            setAnswerHistory(Array.isArray(draft.answerHistory) ? draft.answerHistory : []);
            setCustomPanelData(Object.assign({}, draft.customPanelData || {}, bulkPart));
            setViewIdx(typeof draft.viewIdx === 'number' ? draft.viewIdx : 0);
        } else {
            setSettingsState(Object.assign({}, cs));
            setAnswers({});
            setAnswerHistory([]);
            setCustomPanelData(bulkPart);
            setViewIdx(0);
        }
        // Đánh dấu đã restore xong → cho phép debounced save chạy
        draftReadyRef.current = true;

        var sorted = buildQuestionList(qs, feats);
        setSortedQuestions(sorted);
    }

    // ─── derive câu hiện tại đang xem ──────────────────────────────
    // Pending: trả về câu đầu tiên CHƯA trong answerHistory + visible.
    // KHÔNG skip theo answers map — vì user pick rồi nhưng chưa Next thì display phải GIỮ NGUYÊN câu đó.
    function getCurrentView() {
        if (viewIdx < answerHistory.length) {
            var qId = answerHistory[viewIdx];
            var q = sortedQuestions.find(function (x) { return x.id === qId; });
            return { question: q, isPending: false };
        }
        var inHistory = {};
        answerHistory.forEach(function (id) { inHistory[id] = true; });
        var pending = null;
        for (var i = 0; i < sortedQuestions.length; i++) {
            var qq = sortedQuestions[i];
            if (inHistory[qq.id]) continue;
            if (!isQuestionVisible(qq, settingsState)) continue;
            pending = qq;
            break;
        }
        return { question: pending, isPending: true };
    }

    // Continue button enabled khi user đã có câu trả lời (kể cả Yes/No).
    // Nếu Yes + cấu hình lỗi → onNext sẽ chặn lại + hiện lỗi (không disable button).
    function isQuestionReady(q, ans, panel) {
        if (!q) return false;
        if (q.type === 'qty_with_prefix') {
            return ans === 0 || ans === 1;
        }
        if (q.type === 'multi_pick') return panel[q.id] !== undefined;
        if (q.type === 'custom_panel') {
            var cp = q.customPanel || {};
            if (cp.type === 'product_table_with_ai') {
                var data = panel[q.id];
                var rows = (data && Array.isArray(data.rows)) ? data.rows : [];
                if (rows.length === 0) return true; // skip OK
                for (var i = 0; i < rows.length; i++) {
                    var r = rows[i];
                    var name = String(r && r.name || '').trim();
                    var priceStr = r && r.price != null ? String(r.price).trim() : '';
                    if (!name && !priceStr) continue;
                    if (!name) return false;
                    if (name.length > 250) return false;
                    if (priceStr) {
                        var p = parseFloat(priceStr);
                        if (!Number.isFinite(p) || p < 0) return false;
                    }
                }
                return true;
            }
            if (cp.type === 'slot_table_with_ai') {
                var sdata = panel[q.id];
                var srows = (sdata && Array.isArray(sdata.rows)) ? sdata.rows : [];
                if (srows.length === 0) return true; // skip OK — wizard sẽ giữ default seed
                for (var k = 0; k < srows.length; k++) {
                    var sr = srows[k];
                    var code = String(sr && sr.slot_code || '').trim();
                    var startStr = String(sr && sr.start_hour || '').trim();
                    var endStr = String(sr && sr.end_hour || '').trim();
                    if (!code && !startStr && !endStr) continue;
                    if (!code) return false;
                    if (!/^\d{1,2}:\d{2}(:\d{2})?$/.test(startStr)) return false;
                    if (!/^\d{1,2}:\d{2}(:\d{2})?$/.test(endStr)) return false;
                    if (endStr <= startStr) return false;
                    var wp = Number(sr.weekday_price);
                    if (!Number.isFinite(wp) || wp < 0) return false;
                }
                return true;
            }
            return panel[q.id] !== undefined;
        }
        return ans !== undefined && ans !== null;
    }

    // Validate trước khi advance. Trả về null nếu OK, hoặc string lỗi.
    function validateBeforeNext(q, ans, panel, locale) {
        if (!q) return null;
        if (q.type === 'qty_with_prefix' && ans === 1) {
            var data = panel[q.id];
            var areas = (data && Array.isArray(data.areas)) ? data.areas : [];
            if (areas.length === 0) {
                return t(locale, 'areas_need_row');
            }
            var v = validateAreas(areas, q.customPanel || {}, locale);
            if (v.errors.length > 0) return v.errors[0];
            if (v.validCount === 0) {
                return t(locale, 'areas_need_valid');
            }
        }
        return null;
    }

    // User chọn đáp án: chỉ set answers[qId], KHÔNG auto-advance.
    // value có thể là 0/1 (Yes/No) hoặc string (multi-choice).
    function onPick(qId, value) {
        var v;
        if (value === 1 || value === 0) v = value;
        else if (typeof value === 'string' && value.length > 0) v = value;
        else return; // invalid value — bỏ qua
        setAnswers(function (prev) { return Object.assign({}, prev, { [qId]: v }); });
    }

    function onPanelChange(qId, data) {
        setCustomPanelData(function (prev) { return Object.assign({}, prev, { [qId]: data }); });
    }

    // Pick + auto-advance (cho Yes/No tại câu pending — đáp án 1 chạm).
    // - Pending: commit answer vào history + advance viewIdx + apply state.
    // - Revisit (đang xem lại câu cũ): chỉ set answer, KHÔNG advance — user click Next manual.
    async function onPickAndNext(qId, value) {
        var v;
        if (value === 1 || value === 0) v = value;
        else if (typeof value === 'string' && value.length > 0) v = value;
        else return;

        var view = getCurrentView();
        if (!view.question || view.question.id !== qId) return;

        if (!view.isPending) {
            // Revisit: chỉ set answer, đợi user click Next để cascade rebuild
            setAnswers(function (prev) { return Object.assign({}, prev, { [qId]: v }); });
            return;
        }

        // qty_with_prefix + Yes → cần fill panel, init defaults, KHÔNG advance
        if (view.question.type === 'qty_with_prefix' && v === 1) {
            setAnswers(function (prev) { return Object.assign({}, prev, { [qId]: 1 }); });
            if (!customPanelData[qId]) {
                var defaults = (view.question.customPanel && view.question.customPanel.defaults) || [];
                var clone = defaults.map(function (f) { return Object.assign({}, f); });
                setCustomPanelData(function (prev) {
                    return Object.assign({}, prev, { [qId]: { areas: clone } });
                });
            }
            return;
        }

        // Pending: commit + advance (tính inline, không đọc từ state stale)
        var newAnswers = Object.assign({}, answers, { [qId]: v });
        var newHistory = answerHistory.concat([qId]);
        var newState = applyAnswer(settingsState, view.question, v);
        var newViewIdx = newHistory.length;

        setAnswers(newAnswers);
        setAnswerHistory(newHistory);
        setSettingsState(newState);
        setViewIdx(newViewIdx);

        var checkNext = getNextQuestion(sortedQuestions, newAnswers, newState, customPanelData);
        if (!checkNext) {
            await finishSetup(newState);
        }
    }

    // Đi tiếp: validate có answer → commit (push history nếu pending, hoặc cascade-rebuild nếu sửa).
    async function onNext() {
        var view = getCurrentView();
        if (!view.question) return;
        var qId = view.question.id;
        var ans = answers[qId];
        if (!isQuestionReady(view.question, ans, customPanelData)) return;   // chưa trả lời → noop (button đã disabled)

        // Cấu hình lỗi → block + báo lỗi (không advance)
        var validationErr = validateBeforeNext(view.question, ans, customPanelData, locale);
        if (validationErr) { setError(validationErr); return; }
        setError(null);

        var nextHistory, nextAnswers, nextState;

        if (view.isPending) {
            // Lần đầu trả lời câu này
            nextHistory = answerHistory.concat([qId]);
            nextAnswers = answers;            // đã có answers[qId] rồi
            nextState = applyAnswer(settingsState, view.question, ans);
        } else {
            // Đang xem lại câu đã trả lời. Có thể answer đã được sửa.
            // Cascade rebuild: walk full history với answers mới, drop câu đã invisible.
            var rb = rebuildHistoryAndState(currentSettings, sortedQuestions, answers, answerHistory, customPanelData);
            nextHistory = rb.history;
            nextAnswers = rb.answers;
            nextState = rb.settingsState;
            setCustomPanelData(rb.customPanelData);
        }

        // Tính viewIdx mới: position của qId trong nextHistory + 1 (qua câu kế).
        var pos = nextHistory.indexOf(qId);
        var newViewIdx = (pos >= 0) ? pos + 1 : nextHistory.length;

        // Nếu newViewIdx vượt quá history → đang ở pending. Check còn pending không.
        var checkNext = null;
        if (newViewIdx >= nextHistory.length) {
            checkNext = getNextQuestion(sortedQuestions, nextAnswers, nextState, customPanelData);
        }

        setAnswerHistory(nextHistory);
        setAnswers(nextAnswers);
        setSettingsState(nextState);
        setViewIdx(newViewIdx);

        if (newViewIdx >= nextHistory.length && !checkNext) {
            await finishSetup(nextState);
        }
    }

    // Quay lại câu trước: chỉ giảm viewIdx, GIỮ nguyên answers + history.
    // Dùng functional updater để tránh stale closure (luôn đọc viewIdx mới nhất).
    // Tại viewIdx=0 (câu đầu) thì noop — không có bước nào trước Stage 1.
    function onBack() {
        setViewIdx(function (prev) { return prev > 0 ? prev - 1 : 0; });
    }

    // Setup chỉ cấu hình nghiệp vụ. Chọn/mua tier của bất kỳ dịch vụ nào đều đi
    // qua màn quản lý dịch vụ và checkout riêng.
    async function finishSetup(stateToUse) {
        await applyDirect(stateToUse || settingsState);
    }

    async function applyDirect(stateToUse) {
        var d = await call('apply', {
            settingsState: stateToUse,
            answers: answers,
            customPanelData: customPanelData
        });
        if (d && d.ok) {
            try { localStorage.removeItem(bulkStorageKey); } catch (_) { /* ignore */ }
            window.location.href = '/';
        }
    }

    function renderIdentityBanner() {
        if (!identity) return null;
        var bits = [];
        if (identity.business_name) bits.push(identity.business_name);
        var indName = identity.industry_name && (identity.industry_name[locale] || identity.industry_name.vi || identity.industry_name.en);
        if (indName) bits.push(t(locale, 'identity_industry') + ': ' + indName);
        else if (identity.industry) bits.push(t(locale, 'identity_industry') + ': ' + identity.industry);
        return <div className="wiz-identity-banner">{bits.join(' • ')}</div>;
    }

    function onLogout() {
        try { AntAuth.logOut(); } catch (e) {
            try { sessionStorage.clear(); localStorage.clear(); } catch (_) {}
            window.location.reload();
        }
    }

    var view = getCurrentView();
    var currentQuestion = view.question;
    var currentAnswer = currentQuestion ? answers[currentQuestion.id] : undefined;
    var pendingCount = getVisiblePendingCount(sortedQuestions, answers, settingsState, customPanelData);
    var answeredCount = answerHistory.length;
    var totalEstimate = answeredCount + pendingCount;
    var positionLabel = (viewIdx + 1);  // 1-based
    var canBack = viewIdx > 0;
    var canNext = isQuestionReady(currentQuestion, currentAnswer, customPanelData);

    React.useEffect(function () {
        if (!currentQuestion) return;
        if (currentQuestion.type === 'multi_pick' && customPanelData[currentQuestion.id] === undefined) {
            setCustomPanelData(function (prev) {
                return Object.assign({}, prev, { [currentQuestion.id]: { codes: [] } });
            });
        }
        // custom_panel: init { rows: [] } để user có thể click Next mà không cần nhập gì
        // (skip-OK với product_table_with_ai). _isAnswered() đọc panel[qId] → cần defined.
        if (currentQuestion.type === 'custom_panel' && customPanelData[currentQuestion.id] === undefined) {
            var cp = currentQuestion.customPanel || {};
            var initData = (cp.type === 'product_table_with_ai' || cp.type === 'slot_table_with_ai') ? { rows: [] } : {};
            setCustomPanelData(function (prev) {
                return Object.assign({}, prev, { [currentQuestion.id]: initData });
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentQuestion ? currentQuestion.id : null]);

    var isWideCustomPanel = currentQuestion && currentQuestion.type === 'custom_panel'
        && currentQuestion.customPanel
        && (currentQuestion.customPanel.type === 'product_table_with_ai'
            || currentQuestion.customPanel.type === 'slot_table_with_ai');
    var rootClass = 'ant-setup-wizard' + (isWideCustomPanel ? ' ant-setup-wizard--wide' : '');

    return (
        <div className={rootClass}>
            {error ? <div className="wiz-error">{error}</div> : null}
            {loading ? <div className="wiz-loading">{t(locale, 'loading')}</div> : null}
            {currentQuestion ? (
                <Stage2Round
                    locale={locale}
                    question={currentQuestion}
                    currentAnswer={currentAnswer}
                    positionLabel={positionLabel}
                    totalEstimate={totalEstimate}
                    canBack={canBack}
                    canNext={canNext}
                    isPending={view.isPending}
                    panelData={customPanelData[currentQuestion.id]}
                    currency={currentSettings.Sys_BaseCurrency || null}
                    aiProviderReady={aiProviderReady}
                    onPanelChange={onPanelChange}
                    onPick={onPick}
                    onPickAndNext={onPickAndNext}
                    onNext={onNext}
                    onBack={onBack}
                />
            ) : null}
            <div className="wiz-topbar wiz-topbar--no-steps">
                <button type="button" className="wiz-logout-btn" onClick={onLogout}>{t(locale, 'logout')}</button>
            </div>
        </div>
    );
}
